import emojiRegex from 'emoji-regex-xs';
import localeService from 'src/dataServices/LocaleService';
import { IAddressResources } from 'src/state/profile/types';
import FieldValidationError from './FieldValidationError';

export const inputTextFieldComponent = (field: any) => {
  const {
    input,
    type,
    id,
    name,
    label,
    ariaLabel,
    required,
    readOnly,
    placeholder,
    maxLength,
    autoFocus,
    disabled,
    meta: { error, touched, active },
    hasServerValidationError
  } = field;
  return (
    <div>
      <input
        {...input}
        type={type}
        id={id}
        name={name}
        label={label}
        aria-label={ariaLabel}
        required={required}
        placeholder={placeholder}
        readOnly={readOnly}
        autoFocus={autoFocus}
        maxLength={maxLength}
        disabled={disabled}
        className="form-control"
        onChange={(e) => {
          input.onChange(e);
          if (e.target.value.trim() === '') {
            input.onBlur();
          }
        }}
      />
      {(hasServerValidationError || (touched && error)) && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

export const inputTextAreaComponent = (field: any) => {
  const {
    input,
    id,
    name,
    label,
    rows,
    required,
    readOnly,
    placeholder,
    maxLength,
    autoFocus,
    disabled,
    meta: { error, touched }
  } = field;
  return (
    <div>
      <textarea
        {...input}
        id={id}
        name={name}
        label={label}
        rows={rows}
        required={required}
        placeholder={placeholder}
        readOnly={readOnly}
        autoFocus={autoFocus}
        maxLength={maxLength}
        disabled={disabled}
        className="form-control"
      />
      {touched && error && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

export const composeRequiredFieldValidator = (name: string) => (value: string) =>
  value && value.trim() ? undefined : name + 'RequiredError';

export const requiredFieldValidator = (value: string, name: string) =>
  value && value.trim() ? undefined : name + 'RequiredError';

export const emailRequiredFieldValidator = (value: string) =>
  value && value.trim() ? undefined : 'EmailRequiredError';

export const emailAddressValidator = (value: string) =>
  value && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/i.test(value) ? 'EmailInvalidError' : undefined;

const websiteUrlValidator = (value: string, name: string) =>
  value &&
  !/^(https?:\/\/)(?!.*@)(www\.)?[-a-zA-Z0-9:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9:%_\+.~#?&//=]*)/i.test(value)
    ? name + 'InvalidError'
    : undefined;

export const composeWebsiteUrlValidator = (name: string) => (value: string) => websiteUrlValidator(value, name);

const phoneNumberValidator = (value: string, name: string) =>
  value && !/^\d*$/i.test(value) ? name + 'InvalidError' : undefined;

export const composePhoneNumberValidator = (name: string) => (value: string) => phoneNumberValidator(value, name);

const NumberOnlyValidator = (value: string, name: string) =>
  value && !/^\d*$/i.test(value) ? name + 'InvalidError' : undefined;

export const composeNumberOnlyValidator = (name: string) => (value: string) => NumberOnlyValidator(value, name);

// banned input strings
const bannedFirstAndLastNameInputs: Record<string, true> = {
    "admin": true,
    "administrator": true,
    "test": true,
    "developer": true,
    "n/a": true,
    "无": true,
    "nadie": true,
    "abc": true,
    "xyz": true,
    "123": true,
    "aabb": true
};

const bannedMiddleNameInputs: Record<string, true> = {
    "abc": true,
    "xyz": true,
    "123": true,
    "aabb": true
};

export const hasEmojisValidation = (validationMessage: string | null) => (value: string) =>
  value && emojiRegex().test(value) ? validationMessage : undefined;

export const hasBannedFirstAndLastNameInputValidation = (validationMessage: string | null) => (value: string) =>
  value && bannedFirstAndLastNameInputs[value.toLowerCase()] ? validationMessage : undefined;

export const hasBannedMiddleNameInputValidation = (validationMessage: string | null) => (value: string) =>
  value && bannedMiddleNameInputs[value.toLowerCase()] ? validationMessage : undefined;

export const startsOrEndsWithSpaceValidation = (validationMessage: string | null) => (value: string) =>
  value && value.trim() !== value ? validationMessage : undefined;

export const onlyNonprintableCharactersValidation = (validationMessage: string | null) => (value: string) =>
  value && /^[\x00-\x1F\x7F]+$/.test(value) ? validationMessage : undefined;

export const onlySpecialCharactersValidation = (validationMessage: string | null) => (value: string) =>
  value && /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]+$/.test(value) ? validationMessage : undefined;

export const hasNonprintableCharactersValidation = (validationMessage: string | null) => (value: string) =>
  value && /[\x00-\x1F\x7F]/.test(value) ? validationMessage : undefined;

export const isNumericValidation = (validationMessage: string | null) => (value: string) =>
  value && /^\d+$/.test(value) ? validationMessage : undefined;

export const minLengthValidation = (validationMessage: string | null, minLength: number) => (value: string) =>
  value && value.length < minLength ? validationMessage : undefined;

export const maxLengthValidation = (validationMessage: string | null, maxLength: number) => (value: string) =>
  value && value.length > maxLength ? validationMessage : undefined;

const bannedAbbreviations: Record<string, true> = {
    " c p a": true,
    "c.p.a.": true,
    "l.l.p.": true,
    "c. p. a.": true,
    "l. l. p.": true,
    " llc": true,
    "l. l. c.": true,
    "l.l.c.": true,
    " llp": true,
    "corp": true,
    "corporation": true,
    "inc": true,
    "inc.": true,
    "corp.": true,
    "llc.": true,
    "llp.": true,
    "incorporated": true,
    "limited": true,
    "llc": true,
    "llp": true,
    "ltd": true,
    "ltd.": true,
    "sdn bhd": true,
    "pty": true,
    "pty ltd": true,
    "pte ltd": true,
    "private limited": true,
    "pvt": true,
    "pvt ltd": true,
    "gmbh": true,
    "plc": true,
    "wll": true,
    "lda": true,
    "sarl": true,
    "kft": true,
    "ltda": true,
    "zrt": true,
    "ooo": true
};

export const isBannedAbbreviationValidation = (validationMessage: string | null) => (value: string) =>
  value && bannedAbbreviations[value.toLowerCase()] ? validationMessage : undefined;

export const onlySpacesOrTabsValidation = (validationMessage: string | null) => (value: string) =>
  value && /^[ \t]*$/.test(value) ? validationMessage : undefined;

export const spacesOrTabsMixedWithIndividualCharacters = (validationMessage: string | null) => (value: string) =>
  value && /^([ \t]*\w[ \t]*)+$/.test(value) ? validationMessage : undefined;

const bannedOrgNameInputs: Record<string, true> = {
    "na": true,
    "n/a": true,
    "none": true
};

export const hasBannedOrgNameInputValidation = (validationMessage: string | null) => (value: string) =>
  value && bannedOrgNameInputs[value.toLowerCase()] ? validationMessage : undefined;

export const getProfileOrganizationSizeOptions = () => {
  return [
    { key: '', text: localeService.getText('Profile', 'OrganizationSizeOptionEmpty') },
    { key: '0-50', text: localeService.getText('Profile', 'OrganizationSizeOption0-50') },
    { key: '51-100', text: localeService.getText('Profile', 'OrganizationSizeOption51-100') },
    { key: '101-250', text: localeService.getText('Profile', 'OrganizationSizeOption101-250') },
    { key: '251-500', text: localeService.getText('Profile', 'OrganizationSizeOption251-500') },
    { key: '501-1000', text: localeService.getText('Profile', 'OrganizationSizeOption501-1000') },
    { key: '1000+', text: localeService.getText('Profile', 'OrganizationSizeOption1000+') }
  ];
};

export const getProfileOrganizationSizeText = (orgSize: string): string => {
  let text = '-';

  if (!orgSize) {
    return text;
  }

  const options = getProfileOrganizationSizeOptions();
  if (!options || options.length === 0) {
    return text;
  }

  const option = options.find((option) => option.key === orgSize);
  text = option ? option.text || '-' : '-';

  return text;
};

const legalIdentifierValidators: Record<any, (value: string, formValues: any) => string | undefined> = {};

export const getLegalIdentifierValidator = (
  countryCode: string,
  identifierKey: string,
  regexPattern: RegExp,
  label: string
) => {
  const key = countryCode + '_' + identifierKey;
  let val = legalIdentifierValidators[key];
  if (val === undefined || val == null) {
    val = (value: string, formValues: any) => validateLegalIdentifier(regexPattern, value, label);
    legalIdentifierValidators[key] = val;
  }
  return val;
};

export const regulatoryBodyRequiredFieldValidator = (value: string) =>
  value && value.trim() ? undefined : 'regulatoryBodyRequiredError';

export const legalIdentifierRequiredFieldValidator = (value: string) =>
  value && value.trim() ? undefined : 'legalIdentifierRequiredError';

export const validateLegalIdentifier = (
  regexPattern: RegExp,
  identifier: string,
  label: string
): string | undefined => {
  if (!identifier || identifier.trim() === '') {
    return `${label} is required`;
  }

  if (regexPattern && !regexPattern.test(identifier)) {
    return `${label} format is invalid`;
  }

  return undefined;
};

const postalCodeValidators: Record<any, (value: string, formValues: any) => string | undefined> = {};
export const getPostalCodeValidator = (addressResources: IAddressResources) => {
  const key = addressResources !== undefined ? addressResources.countryCode : undefined;
  let val = postalCodeValidators[key as string];
  if (val === undefined || val == null) {
    val = (value: any, formValues: any) => postalCodeValidator(value, addressResources);
    postalCodeValidators[key as string] = val;
  }
  return val;
};

export const postalCodeValidator = (value: any, addressResources: IAddressResources) => {
  const postalCodeValidator =
    addressResources && addressResources.addressFields
      ? addressResources.addressFields.filter((x) => x.fieldID === 'PostalCode')[0]
      : undefined;
  const postalCodeRegex =
    postalCodeValidator && postalCodeValidator.fieldRegex ? RegExp(postalCodeValidator.fieldRegex) : undefined;
  const postalCodeExample =
    postalCodeValidator && postalCodeValidator.fieldExample ? postalCodeValidator.fieldExample : undefined;

  if (postalCodeRegex && !postalCodeRegex.test(value)) {
    return localeService
      .getText('ProfileV2', 'address.postalCodeInvalidError')
      ?.replace('{postalCodeExample}', postalCodeExample as any);
  }

  return undefined;
};

export const composeValidators = (validators: any[] | undefined) => (value: any) => {
  if (!validators) {
    return validators;
  }

  return validators.reduce((error, validator) => error || validator(value), undefined);
};
